// GA-07 por mes (bloque 5d, D): el informe de actividades del Técnico o Servicio
// Externo es mensual (AM 196 Art. 13). Un mes cuenta si hay un informe GA-07
// cuyo "Aprobado desde" cae en ese mes; la fecha de carga no cuenta.

export const MESES_CORTOS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'] as const;
export const MESES_LARGOS = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
] as const;

export interface InformeGA07 {
  id: string;
  file_url: string;
  approved_from: string | null;
  is_current: boolean;
  created_at: string;
}

export type EstadoMes = 'con_informe' | 'falta' | 'futuro';

export interface MesGA07 {
  mes: number;              // 1 a 12
  estado: EstadoMes;
  informe: InformeGA07 | null;
}

/** "2026-03-01" para marzo de 2026: el "Aprobado desde" al subir desde un mes vacío. */
export function primerDiaMes(anio: number, mes: number): string {
  return `${anio}-${String(mes).padStart(2, '0')}-01`;
}

// Si un mes tiene varios informes se abre la versión vigente; entre iguales, el más reciente.
function mejorInforme(a: InformeGA07, b: InformeGA07): InformeGA07 {
  if (a.is_current !== b.is_current) return a.is_current ? a : b;
  return a.created_at >= b.created_at ? a : b;
}

/**
 * Los 12 meses de `anio`. Se cuentan también las versiones archivadas: si se
 * sube cada mes con el mismo título y categoría, la del mes anterior queda
 * archivada, pero el informe de ese mes existió.
 */
export function mesesGA07(informes: InformeGA07[], anio: number, hoy: string): MesGA07[] {
  const [anioHoy, mesHoy] = hoy.split('-').map(Number);
  const porMes = new Map<number, InformeGA07>();
  for (const inf of informes) {
    if (!inf.approved_from) continue;
    const [a, m] = inf.approved_from.split('-').map(Number);
    if (a !== anio) continue;
    const previo = porMes.get(m);
    porMes.set(m, previo ? mejorInforme(previo, inf) : inf);
  }
  return MESES_CORTOS.map((_, i) => {
    const mes = i + 1;
    const informe = porMes.get(mes) ?? null;
    const futuro = anio > anioHoy || (anio === anioHoy && mes > mesHoy);
    // Un informe con fecha futura se muestra igual (se puede abrir).
    const estado: EstadoMes = informe ? 'con_informe' : futuro ? 'futuro' : 'falta';
    return { mes, estado, informe };
  });
}

/** Años para el selector: el actual, el anterior y los que tengan informes; del más reciente al más antiguo. */
export function aniosGA07(informes: InformeGA07[], hoy: string): number[] {
  const actual = Number(hoy.slice(0, 4));
  const anios = new Set<number>([actual, actual - 1]);
  for (const inf of informes) if (inf.approved_from) anios.add(Number(inf.approved_from.slice(0, 4)));
  return Array.from(anios).sort((a, b) => b - a);
}

/** Informes GA-07 sin "Aprobado desde": no se pueden ubicar en un mes. */
export function informesSinFecha(informes: InformeGA07[]): number {
  return informes.filter(i => !i.approved_from).length;
}
